import logger from '../utils/logger';
import { useState, useEffect, useCallback } from "react";

/**
 * Fetches custom body parts, custom imaging orders, and base entry overrides
 * from the API and merges them with the hardcoded base catalog maps.
 *
 * @param {Object} baseAnatomicalMap     - The hardcoded anatomicalMap from NewImagingOrder.js
 * @param {Object} baseRegionBodyPartsMap - The hardcoded regionBodyPartsMap from NewImagingOrder.js
 * @returns {{ mergedAnatomicalMap, mergedRegionBodyPartsMap, catalogLoading, refetchCatalog }}
 */
const useImagingCatalog = (baseAnatomicalMap, baseRegionBodyPartsMap) => {
  const BASE_URL = process.env.REACT_APP_API_URL;

  const [mergedAnatomicalMap, setMergedAnatomicalMap] = useState(baseAnatomicalMap);
  const [mergedRegionBodyPartsMap, setMergedRegionBodyPartsMap] = useState(baseRegionBodyPartsMap);
  const [catalogLoading, setCatalogLoading] = useState(true);

  const fetchAndMerge = useCallback(async () => {
    setCatalogLoading(true);
    try {
      const response = await fetch(`${BASE_URL}/orders/customCatalog`);
      if (!response.ok) {
        logger.error("Failed to fetch custom imaging catalog");
        return;
      }

      const { customOrders = [], customBodyParts = [], overrides = [] } = await response.json();

      // --- Merge body parts ---
      const newRegionBodyPartsMap = {};
      Object.keys(baseRegionBodyPartsMap).forEach((region) => {
        newRegionBodyPartsMap[region] = [...baseRegionBodyPartsMap[region]];
      });

      customBodyParts.forEach(({ region, name }) => {
        if (!newRegionBodyPartsMap[region]) newRegionBodyPartsMap[region] = [];
        if (!newRegionBodyPartsMap[region].includes(name)) {
          newRegionBodyPartsMap[region].push(name);
        }
      });

      // --- Merge anatomical map (apply overrides, filter hidden, append custom) ---
      const newAnatomicalMap = {};
      Object.keys(baseAnatomicalMap).forEach((region) => {
        newAnatomicalMap[region] = baseAnatomicalMap[region]
          .filter((entry) => !overrides.some((o) => o.base_id === entry.id && o.is_hidden))
          .map((entry) => {
            const override = overrides.find((o) => o.base_id === entry.id && !o.is_hidden);
            if (!override) return entry;
            return {
              ...entry,
              code: override.cpt_code ?? entry.code,
              name: override.name ?? entry.name,
            };
          });
      });

      customOrders.forEach(({ id, region, cpt_code, name, modality }) => {
        if (!newAnatomicalMap[region]) newAnatomicalMap[region] = [];
        newAnatomicalMap[region].push({
          id: `custom_${id}`,
          code: cpt_code,
          name,
          modality,
        });
      });

      setMergedAnatomicalMap(newAnatomicalMap);
      setMergedRegionBodyPartsMap(newRegionBodyPartsMap);
    } catch (error) {
      logger.error("Error fetching custom imaging catalog:", error);
    } finally {
      setCatalogLoading(false);
    }
  }, [BASE_URL, baseAnatomicalMap, baseRegionBodyPartsMap]);

  useEffect(() => {
    fetchAndMerge();
  }, [fetchAndMerge]);

  return {
    mergedAnatomicalMap,
    mergedRegionBodyPartsMap,
    catalogLoading,
    refetchCatalog: fetchAndMerge,
  };
};

export default useImagingCatalog;
